import React from 'react';
import {Icon} from '@/components/icons/Icon';

export type BadgeVariant = 
  | 'primary' 
  | 'secondary' 
  | 'success' 
  | 'danger' 
  | 'warning' 
  | 'info' 
  | 'light' 
  | 'dark';

export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'size'> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  pill?: boolean;
  outline?: boolean;
  icon?: string;
  iconPosition?: 'left' | 'right';
  spacing?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
  'data-cy'?: string;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(({
    variant = 'secondary',
    size = 'md',
    pill = false,
    outline = false,
    icon,
    iconPosition = 'left',
    spacing = 'sm',
    className = '',
    children,
    ...props
}, ref) => {
    const baseClasses = 'badge';
  
    const variantClasses = {
        primary: outline ? 'badge-outline-primary' : 'badge-primary',
        secondary: outline ? 'badge-outline-secondary' : 'badge-secondary',
        success: outline ? 'badge-outline-success' : 'badge-success',
        danger: outline ? 'badge-outline-danger' : 'badge-danger',
        warning: outline ? 'badge-outline-warning' : 'badge-warning',
        info: outline ? 'badge-outline-info' : 'badge-info',
        light: outline ? 'badge-outline-light' : 'badge-light',
        dark: outline ? 'badge-outline-dark' : 'badge-dark'
    };

    const sizeClasses = {
        xs: 'badge-xs',
        sm: 'badge-sm',
        md: '',
        lg: 'badge-lg'
    };

    const sizeStyles = {
        xs: {fontSize: '10px', padding: '2px 6px'},
        sm: {fontSize: '11px', padding: '3px 8px'},
        md: {fontSize: '12px', padding: '4px 10px'},
        lg: {fontSize: '14px', padding: '6px 12px'}
    };

    const classes = [
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        pill ? 'badge-pill' : '',
        className
    ].filter(Boolean).join(' ');

    const spacingValues = {
        none: '0',
        xs: '4px',
        sm: '8px',
        md: '12px',
        lg: '16px'
    };

    const badgeStyle = {
        ...sizeStyles[size],
        display: 'inline-flex',
        alignItems: 'center',
        gap: icon ? '4px' : '0',
        verticalAlign: 'middle',
        marginLeft: spacingValues[spacing],
        ...props.style
    };

    const renderIcon = () => {
        if (!icon) {return null;}
    
        const iconSize = size === 'xs' ? '0.75' : size === 'sm' ? '0.875' : size === 'lg' ? '1.2' : '1';
        return <Icon name={icon} size={iconSize} />;
    };

    const content = (
        <>
            {icon && iconPosition === 'left' && renderIcon()}
            {children}
            {icon && iconPosition === 'right' && renderIcon()}
        </>
    );

    return (
        <span
            ref={ref}
            className={classes}
            style={badgeStyle}
            {...props}
        >
            {content}
        </span>
    );
});

Badge.displayName = 'Badge';

export default Badge;
